import PropTypes from 'prop-types';
import React, { useCallback } from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CheckExampleGraphic from 'assets/images/transfers/CheckExampleGraphic.svg';
import CheckExampleGraphicDark from 'assets/images/transfers/CheckExampleGraphicDark.svg';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  grid: {
    display: 'flex',
    flexDirection: 'row',
    '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]': {
      MozAppearance: 'none',
    },
    '&.margin': {
      marginBottom: 10,
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  textField: {
    marginRight: 20,
    minWidth: 345,
    width: 345,
  },
  checkWrapper: {
    marginLeft: 435,
    marginTop: -100,
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
  },
  checkTextWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  textRow: {
    alignItems: 'center',
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'row',
  },
  linkItem: {
    marginTop: 3, // Align link to typography. Link seems to have different alignment
    alignItems: 'center',
  },
  text: {
    marginRight: 2,
    marginTop: 4,
  },
  bottomMessage: {
    paddingLeft: theme.spacing(2),
    marginTop: 0,
    display: 'flex',
    flexDirection: 'row',
    '&.negativeMargin': {
      marginTop: -15,
    },
  },
  icon: {
    height: 16,
    width: 16,
  },
  infoIcon: {
    marginTop: -8,
    marginLeft: 2,
  },
}));

/* Text for the Info Icon Tooltip! */
const tooltipHTML = (
  <span>
    To find your routing and account numbers, search for “direct deposits” or “ACH transfers”.
    <br />
    <br />
    If you’re still having trouble, check your institution’s FAQ page or contact them directly.
  </span>
);

const SelectAccountForm = ({
  account,
  accountNumber,
  routingNumber,
  accountNumberConfirm,
  onChange,
  fieldErrors,
  institution,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();

  const onChangeProxy = useCallback(
    (newSelected, newAccountNumber, newRoutingNumber, newAccountNumberConfirm) => {
      if (newSelected) {
        const newAccountNumberMaxed = newAccountNumber?.length > 17 ? newAccountNumber.slice(0, 17) : newAccountNumber;
        const newAccountNumberConfirmMaxed =
          newAccountNumberConfirm?.length > 17 ? newAccountNumberConfirm.slice(0, 17) : newAccountNumberConfirm;
        onChange({
          id: account?.id,
          accountNumberConfirm: newAccountNumberConfirmMaxed,
          accountNumber: newAccountNumberMaxed,
          routingNumber: newRoutingNumber?.length > 9 ? newRoutingNumber.slice(0, 9) : newRoutingNumber,
        });
      } else {
        onChange(null);
      }
    },
    [onChange, account?.id],
  );

  // Only numerical values are allowed through the use of regular expressions!
  // We don't use type='number' because that allows negatives and 'e'
  const onlyNumbers = (value) => /^\d*$/.test(value);

  const handleRoutingNumberChange = (e) => {
    if (onlyNumbers(e.target.value)) {
      onChangeProxy(true, accountNumber, e.target.value, accountNumberConfirm);
    }
  };

  const handleAccountNumberChange = (e) => {
    if (onlyNumbers(e.target.value)) {
      onChangeProxy(true, e.target.value, routingNumber, accountNumberConfirm);
    }
  };

  const handleAccountNumberConfirmChange = (e) => {
    if (onlyNumbers(e.target.value)) {
      onChangeProxy(true, accountNumber, routingNumber, e.target.value);
    }
  };

  return (
    <div className={classes.root}>
      <div className={cx(classes.grid, 'margin')}>
        <TextField
          className={classes.textField}
          value={routingNumber || ''}
          onChange={handleRoutingNumberChange}
          label='9-digit routing number'
          variant='filled'
          inputProps={{ maxLength: 9 }}
          error={Boolean(fieldErrors?.routingNumber)}
          helperText={fieldErrors?.routingNumber || ' '}
        />
        <div className={classes.checkWrapper}>
          <img src={theme.palette.mode === 'dark' ? CheckExampleGraphicDark : CheckExampleGraphic} alt='check' />
          <div className={classes.textRow}>
            {institution?.url && (
              <Link
                className={classes.linkItem}
                href={institution?.url}
                target='_blank'
                underline='hover'
                variant='captionRegular'
                color='primary'
              >
                Visit institution &nbsp;
                <OpenInNewIcon className={classes.icon} />
              </Link>
            )}
            <div className={classes.checkTextWrapper}>
              <Typography variant='captionRegular' color='textSecondary' className={classes.text}>
                Where to find?
              </Typography>
              <InfoTooltipIcon size='small' tooltipPlacement='right' tooltipHTML={tooltipHTML} />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.grid}>
        <TextField
          className={classes.textField}
          value={accountNumber || ''}
          onChange={handleAccountNumberChange}
          label='Account number'
          variant='filled'
          error={Boolean(fieldErrors?.accountNumber)}
          helperText={fieldErrors?.accountNumber || ' '}
          inputProps={{ maxLength: 17 }}
        />
        <TextField
          className={classes.textField}
          value={accountNumberConfirm || ''}
          onChange={handleAccountNumberConfirmChange}
          label='Confirm account number'
          variant='filled'
          error={Boolean(fieldErrors?.accountNumberConfirm)}
          helperText={fieldErrors?.accountNumberConfirm || ' '}
          inputProps={{ maxLength: 17 }}
        />
      </div>
      <div className={cx(classes.bottomMessage, fieldErrors?.accountNumber ? '' : 'negativeMargin')}>
        <Typography variant='captionRegular' color='text.secondary'>
          Does your account number contain letters?
        </Typography>
        <InfoTooltipIcon
          size='small'
          className={classes.infoIcon}
          tooltipPlacement='right'
          tooltipText='If your account number contains letters, look for a numeric only account number designated for Direct Deposits. You can find this by logging into your institution’s website or by contacting them directly.'
        />
      </div>
    </div>
  );
};
SelectAccountForm.propTypes = {
  account: PropTypes.object.isRequired,
  accountNumber: PropTypes.string,
  accountNumberConfirm: PropTypes.string,
  routingNumber: PropTypes.string,
  onChange: PropTypes.func,
  fieldErrors: PropTypes.object,
  institution: PropTypes.object,
};

export default SelectAccountForm;
